<template>

  <v-card>
    <v-card-title style="word-break: break-word;">
      <v-layout fluid style="vertical-align: text-top;">
        <span>{{ plate.name }}</span>

        <!-- state -->
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon v-on="on" style="margin-left: 8px;">
              mdi-cog
            </v-icon>
          </template>
          <template #default>
            <pre>{{ JSON.stringify(state, null, 2) }}</pre>
          </template>
        </v-tooltip>

        <v-spacer></v-spacer>

        <v-simple-checkbox
          v-model="item.checked"
          :ripple="false"
          style="align-self:start; margin-left: 8px; margin-top: 4px;"
        ></v-simple-checkbox>

      </v-layout>

    </v-card-title>

    <v-card-actions style="margin: 6px;">

      <!-- notes -->
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-icon v-on="on" v-if="plate.notes">
            mdi-message-text-outline
          </v-icon>
          <v-icon v-on="on" v-else>
            mdi-message-outline
          </v-icon>
        </template>
        <div style="max-width: 350px;" v-html="plate.notes"></div>
      </v-tooltip>

      <!-- file -->
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-icon v-on="on" color="#C0FFC0" v-if="plate.file">
            mdi-file-plus-outline
          </v-icon>
          <v-icon v-on="on" color="#E0D800" v-else>
            mdi-file-remove-outline
          </v-icon>
        </template>
        <template #default>
          <span v-if="plate.file">{{ plate.file }}</span>
          <span v-else>No file specified</span>
        </template>
      </v-tooltip>

      <!-- filtersets -->
      <v-tooltip bottom open-delay="400ms">
        <template #activator="{ on }">
          <v-icon v-on="on" color="#C0FFC0" v-if="plate.filtersets_len">
            mdi-palette
          </v-icon>
          <v-icon v-on="on" color="#E0D800" v-else>
            mdi-palette-outline
          </v-icon>
        </template>
        <span v-if="plate.filtersets_len"> Filtersets data present: {{ plate.filtersets_len }} </span>
        <span v-else> No filtersets data </span>
      </v-tooltip>

      <!-- points -->
      <v-tooltip bottom open-delay="400ms">
        <template #activator="{ on }">
          <v-icon v-on="on" color="#C0FFC0" v-if="plate.points_len">
            mdi-clock-time-four
          </v-icon>
          <v-icon v-on="on" color="#E0D800" v-else>
            mdi-clock-time-four-outline
          </v-icon>
        </template>
        <span v-if="plate.points_len"> Data points present: {{ plate.points_len }} </span>
        <span v-else> No data points </span>
      </v-tooltip>

      <v-spacer></v-spacer>

      <!-- extra -->
      <v-tooltip bottom open-delay="400ms" v-if="plate.extra">
        <template #activator="{ on }">
          <v-icon v-on="on" color="#E08000">
            mdi-cog
          </v-icon>
        </template>
        <div>
          Plate with extra data
        </div>
      </v-tooltip>

      <!-- failed -->
      <v-tooltip bottom open-delay="400ms" v-if="plate.failed">
        <template #activator="{ on }">
          <v-icon v-on="on" color="#AA2200">
            mdi-alert-remove-outline
          </v-icon>
        </template>
        <span>Failed</span>
      </v-tooltip>

      <!-- ready -->
      <v-tooltip bottom open-delay="400ms" v-if="!plate.failed">
        <template #activator="{ on }">
          <v-icon color="#FF4466" v-on="on" v-if="plate.ready">
            mdi-heart
          </v-icon>
          <v-icon color="#888888" v-on="on" v-else>
            mdi-heart-outline
          </v-icon>
        </template>
        <span v-if="plate.ready">Ready for analysis</span>
        <span v-else>Not ready</span>
      </v-tooltip>

      <v-spacer></v-spacer>

      <!-- edit -->
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn
            icon v-on="on"
            @click="btnEdit_click"
          >
            <v-icon>mdi-file-document-edit-outline</v-icon>
          </v-btn>
        </template>
        <template #default>
          <span>Edit: {{ plate.id }}</span>
        </template>
      </v-tooltip>

      <v-spacer></v-spacer>

      <!-- project -->
      <project-selector-menu
        v-model="plate.projects"
        @save="$emit('save-projects', $event)"
        :tooltip="{ disabled: false, bottom: true }">
        <template #tooltip>
          <span>Projects of wells' sample</span>
          <template v-if="plate.project_list.length > 0">
            <v-row
              v-for="sample_project_id in plate.project_list"
              :key="sample_project_id">
              <v-col>{{ projects[sample_project_id].name }}</v-col>
            </v-row>
          </template>
          <v-row v-else>
            <v-col>No samples with project</v-col>
          </v-row>
        </template>
      </project-selector-menu>

      <!-- <v-btn :to="get_route(plate)">Open</v-btn>-->
      <router-link :to="get_route(plate)" style="margin-left: 16px;">
        Open
      </router-link>
    </v-card-actions>

  </v-card>

</template>

<script>

	import format from 'string-format'

	import {
		VCard, VCardActions, VCardText,
		VSpacer, VTooltip, VSimpleCheckbox
	} from 'vuetify'

	import ProjectSelectorMenu from "../project/ProjectSelectorMenu"

	import {
		mdiContentSave, mdiCheckboxMultipleMarkedOutline, // project
		mdiContentSaveOutline, // project outline
		mdiMessageTextOutline, mdiMessageOutline, // note
		mdiCog, //extra
		mdiPalette, mdiPaletteOutline, // filtersets
		mdiClockTimeFour, mdiClockTimeFourOutline, // points
		mdiAlertRemoveOutline, // failed
		mdiHeart, mdiHeartOutline, // ready
	} from '@mdi/js'

	import {mapActions, mapGetters} from "vuex"

	export class PlateItem {
		constructor(plate, checked) {
			this._plate = plate
			this.checked = checked
		}

		get plate() {
			return this._plate
		}
	}

	export default {
		name: "plate-list-item",

		components: {
			VCard, VCardActions, VCardText,
			VSpacer, VTooltip, VSimpleCheckbox,
			ProjectSelectorMenu,
		},

		data: function () {
			return {
				selected_project: [],
			}
		},

		props: {
			item: {type: PlateItem, default: () => null,},
		},

		computed: {
			...mapGetters({
				project_list: 'lists/project_list',
				projects: 'lists/projects'
			}),

			plate: function () {
				return this.item.plate
			},

			project_items: function () {
				return this.project_list.filter((p) => p.id > 0)
			},

			state: function () {
				return {
					'plate.ctor.name': this.plate.constructor.name,
					plate: this.plate,
				}
			},
		},

		watch: {
			plate: function (new_value, old_value) {
				// console.debug("PlateListItemComponent.watch.plate ")
				this.selected_project = this.plate.project
			},

			'plate.extra': function () {
				console.debug(format(
					"PlateListItemComponent.watch.'plate.extra'( {0} )",
					this.plate.extra))
			},

			'plate.project': function (new_value, old_value) {
				// console.debug("PlateListItemComponent.watch.plate.project ")
			},

			'item.checked': function () {
				this.$emit('checked_changed', {
					plate: this.plate,
					checked: this.item.checked
				})
			},
		},

		mounted: function () {
			this.selected = this.plate.project
		},

		methods: {
			...mapActions({
				message_add: 'message/add',
			}),

			/**
			 * изготавливает ссылку для планшеты (при отрисовке списка планшет)
			 * @param plate
			 * @returns {{path: string, query: {id: *}}}
			 */
			get_route: function (plate) {
				return {
					path: '/plate',
					query: {
						id: plate.id
					}
				}
			},

			// -- Handle controls' events --

			btnEdit_click: function () {
				this.$emit('edit', this.plate)
			},
		},
	}

</script>